/**
 * Discount Service
 * Manages discounts with localStorage persistence
 */

const STORAGE_KEY = 'geppro_discounts';

// ============================================================
// Storage Helpers
// ============================================================
function loadDiscounts() {
  try {
    const json = localStorage.getItem(STORAGE_KEY);
    return json ? JSON.parse(json) : [];
  } catch {
    return [];
  }
}

function saveDiscounts(discounts) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(discounts));
  } catch (err) {
    console.error('Failed to save discounts:', err);
  }
}

// ============================================================
// In-Memory Database
// ============================================================
const db = {
  discounts: loadDiscounts(),
};

// ============================================================
// Service Methods
// ============================================================
const discountService = {
  /**
   * Fetch all discounts
   */
  async fetchDiscounts() {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve([...db.discounts]);
      }, 150);
    });
  },

  /**
   * Create a new discount
   */
  async createDiscount(payload) {
    return new Promise(resolve => {
      try {
        const { name, description, type, value, code, finalDate, active } = payload;

        // Check if discount with this name already exists
        const exists = db.discounts.some(
          d => d.name.toLowerCase() === name.toLowerCase()
        );

        if (exists) {
          resolve({ error: 'Já existe um desconto com esse nome.' });
          return;
        }

        // Create discount
        const newId = db.discounts.length > 0
          ? Math.max(...db.discounts.map(d => d.id)) + 1
          : 1;

        const newDiscount = {
          id: newId,
          name,
          description: description || '',
          type,
          value,
          code: code || '',
          finalDate: finalDate || null,
          active: active !== false,
          finished: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        db.discounts.push(newDiscount);
        saveDiscounts(db.discounts);

        resolve({ success: true, discount: newDiscount });
      } catch (err) {
        console.error('createDiscount error:', err);
        resolve({ error: 'Erro ao criar desconto' });
      }
    });
  },

  /**
   * Update an existing discount
   */
  async updateDiscount(id, payload) {
    return new Promise(resolve => {
      try {
        const index = db.discounts.findIndex(d => d.id === id);
        if (index === -1) {
          resolve({ error: 'Desconto não encontrado' });
          return;
        }

        const { name, description, type, value, code, finalDate, active } = payload;
        const discount = db.discounts[index];

        // Check duplicate name (excluding current discount)
        const duplicate = db.discounts.some(
          d => d.id !== id && d.name.toLowerCase() === name.toLowerCase()
        );

        if (duplicate) {
          resolve({ error: 'Já existe um desconto com esse nome.' });
          return;
        }

        // Update discount
        db.discounts[index] = {
          ...discount,
          name,
          description: description || '',
          type,
          value,
          code: code || '',
          finalDate: finalDate || null,
          active: active !== false,
          updatedAt: new Date().toISOString(),
        };

        saveDiscounts(db.discounts);
        resolve({ success: true, discount: db.discounts[index] });
      } catch (err) {
        console.error('updateDiscount error:', err);
        resolve({ error: 'Erro ao atualizar desconto' });
      }
    });
  },

  /**
   * Delete a discount
   */
  async deleteDiscount(id) {
    return new Promise(resolve => {
      try {
        const discount = db.discounts.find(d => d.id === id);
        if (!discount) {
          resolve({ error: 'Desconto não encontrado' });
          return;
        }

        db.discounts = db.discounts.filter(d => d.id !== id);
        saveDiscounts(db.discounts);

        resolve({ success: true });
      } catch (err) {
        console.error('deleteDiscount error:', err);
        resolve({ error: 'Erro ao excluir desconto' });
      }
    });
  },

  /**
   * Toggle discount active status
   */
  async toggleDiscountStatus(id) {
    return new Promise(resolve => {
      try {
        const index = db.discounts.findIndex(d => d.id === id);
        if (index === -1) {
          resolve({ error: 'Desconto não encontrado' });
          return;
        }

        // Can't toggle if finished
        if (db.discounts[index].finished) {
          resolve({ error: 'Desconto finalizado não pode ser ativado/desativado' });
          return;
        }

        db.discounts[index].active = !db.discounts[index].active;
        db.discounts[index].updatedAt = new Date().toISOString();
        saveDiscounts(db.discounts);

        resolve({ success: true, discount: db.discounts[index] });
      } catch (err) {
        console.error('toggleDiscountStatus error:', err);
        resolve({ error: 'Erro ao atualizar status' });
      }
    });
  },

  /**
   * Reactivate a finished discount with a new date
   */
  async reactivateDiscount(id, newFinalDate) {
    return new Promise(resolve => {
      try {
        const index = db.discounts.findIndex(d => d.id === id);
        if (index === -1) {
          resolve({ error: 'Desconto não encontrado' });
          return;
        }

        if (!db.discounts[index].finished) {
          resolve({ error: 'Apenas descontos finalizados podem ser reativados' });
          return;
        }

        // Validate new date is in the future
        const newDate = new Date(newFinalDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (newDate <= today) {
          resolve({ error: 'A nova data deve ser futura' });
          return;
        }

        db.discounts[index].finalDate = newFinalDate;
        db.discounts[index].finished = false;
        db.discounts[index].active = true;
        db.discounts[index].updatedAt = new Date().toISOString();
        saveDiscounts(db.discounts);

        resolve({ success: true, discount: db.discounts[index] });
      } catch (err) {
        console.error('reactivateDiscount error:', err);
        resolve({ error: 'Erro ao reativar desconto' });
      }
    });
  },

  /**
   * Auto-finalize expired discounts
   * Should be called on app load and periodically
   */
  async autoFinalizeExpired() {
    return new Promise(resolve => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let finalized = 0;
        db.discounts.forEach(discount => {
          if (discount.finalDate && !discount.finished) {
            const finalDate = new Date(discount.finalDate);
            finalDate.setHours(0, 0, 0, 0);

            if (finalDate < today) {
              discount.finished = true;
              discount.active = false;
              finalized++;
            }
          }
        });

        if (finalized > 0) {
          saveDiscounts(db.discounts);
        }

        resolve({ success: true, finalized });
      } catch (err) {
        console.error('autoFinalizeExpired error:', err);
        resolve({ error: 'Erro ao finalizar descontos expirados' });
      }
    });
  },
};

export default discountService;
